// Blog.jsx - Página principal del blog
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  trackAppStoreClick,
  trackBlogCtaClick,
  trackGooglePlayClick,
  trackNewsletterSignup,
} from '../analytics/googleAnalytics';
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { blogPosts, categories, popularTags } from "../data/blogsposts";
import { SLUGS_RETIRADOS } from "../data/slugsRetirados";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import "./Blog.css";

export default function Blog() {
  const [searchParams] = useSearchParams();
  const tagFromUrl = searchParams.get("tag") || "";
  const isTagPage = Boolean(tagFromUrl);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState(tagFromUrl);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState("");
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  // Filtrar posts por categoría y búsqueda
  // Mismo filtro que scripts/prerender-meta.js: un slug retirado responde 301,
  // asi que listarlo manda al lector a otro articulo del que pincho.
  const filteredPosts = blogPosts.filter(post => {
    if (SLUGS_RETIRADOS[post.slug]) return false;
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  // Post destacado (el más reciente dentro del listado ya ordenado)
  const featuredPost = filteredPosts[0];
  const otherPosts = filteredPosts.slice(1);

  // Newsletter subscription handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) {
      setNewsletterMessage("Por favor ingresa tu email");
      setNewsletterSuccess(false);
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage("");

    // Dynamic API URL based on environment
    const API_URL = window.location.hostname === 'localhost' 
      ? 'http://127.0.0.1:8000/api'
      : 'https://api.wayoai.app/api';

    try {
      const response = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail.trim(),
          source: 'blog'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setNewsletterSuccess(true);
        setNewsletterMessage("¡Gracias! Confirma tu email para recibir artículos.");
        setNewsletterEmail("");
        trackNewsletterSignup('blog');
      } else {
        setNewsletterMessage(data.message || "Error al suscribirse. Intenta nuevamente.");
        setNewsletterSuccess(false);
      }
    } catch (error) {
      setNewsletterMessage("Error de conexión. Intenta más tarde.");
      setNewsletterSuccess(false);
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <>
      {/* La barra superior ya no se pinta aquí: la monta `App.jsx` una sola vez
          para todo el sitio (components/CampanaBar.jsx). Este banner estaba
          copiado en cuatro páginas y ninguna de las tres copias de fuera de la
          portada se enteró de la campaña de septiembre. */}

      <Helmet>
        <title>Blog de Nutrición e IA | Wayo - Artículos sobre Dieta y Fitness</title>
        <meta name="description" content="Blog de nutrición inteligente. Artículos sobre calorías, macronutrientes, pérdida de peso, dieta, fitness y cómo usar IA para mejorar tu alimentación." />
        <meta name="keywords" content="blog nutrición, artículos dieta, calorías, macronutrientes, pérdida peso, fitness, IA nutrición" />
        <link rel="canonical" href="https://wayoai.app/blog" />
        <meta
          name="robots"
          content={isTagPage ? "noindex, follow" : "index, follow, max-image-preview:large"}
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Blog de Nutrición e IA | Wayo" />
        <meta property="og:description" content="Aprende sobre nutrición, dieta y fitness con artículos inteligentes" />
        <meta property="og:url" content="https://wayoai.app/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="blog-page">
      <Header />
      
      <section className="blog-content">
        {/* Hero Section */}
        <div className="blog-hero">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">Blog de Wayo</h1>
            <p className="blog-hero-subtitle">
              Consejos, guías y recursos sobre nutrición, fitness y bienestar
            </p>
          </div>
        </div>

        <div className="blog-container">
          <div className="blog-app-cta blog-app-cta-light" style={{ marginBottom: "24px", background: "linear-gradient(135deg, rgba(1,113,233,0.08) 0%, rgba(0,184,148,0.08) 100%)" }}>
            <div className="blog-app-cta-copy">
              <span className="blog-app-cta-badge">Empieza aquí</span>
              <h2>¿Buscas la mejor app para contar calorías o macros?</h2>
              <p>
                Entra directo a nuestras comparativas y páginas clave para elegir más rápido qué opción te conviene según lo que necesitas.
              </p>
            </div>
            <div className="money-inline-links">
              <Link to="/alternativa-myfitnesspal">Alternativa a MyFitnessPal</Link>
              <Link to="/app-para-contar-calorias-con-foto">App para calorías con foto</Link>
              <Link to="/app-para-contar-macros">App para contar macros</Link>
            </div>
          </div>

          <div className="blog-app-cta" id="blog-app-cta">
            <div className="blog-app-cta-copy">
              <span className="blog-app-cta-badge">App Gratuita</span>
              <h2>Transforma este contenido en resultados reales</h2>
              <p>
                No solo leas sobre nutrición. Escanea tu próxima comida con la IA de Wayo 
                y deja que nosotros calculemos todo por ti en 2 segundos. 
                La forma más rápida de alcanzar tu objetivo.
              </p>
          </div>
            <div className="blog-app-cta-actions">
              <a
                href="https://apps.apple.com/app/id6756500478"
                className="blog-store-badge"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackBlogCtaClick("blog_top_app_store");
                  trackAppStoreClick("blog_top");
                }}
              >
                <img src={appStoreImg} alt="Descargar en App Store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                className="blog-store-badge"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackBlogCtaClick("blog_top_google_play");
                  trackGooglePlayClick("blog_top");
                }}
              >
                <img src={googlePlayImg} alt="Descargar en Google Play" />
              </a>
            </div>
          </div>

          {/* Buscador y filtros */}
          <div className="blog-filters">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Buscar artículos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="categories-filter">
              <button
                className={`category-btn ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                Todos
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.name ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.name)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Newsletter Mobile - Solo visible en móvil, debajo de filtros */}
          <div className="newsletter-mobile">
            <div className="sidebar-widget newsletter-widget">
              <h3>📧 Newsletter</h3>
              <p>Recibe consejos de nutrición en tu email</p>
              
              {newsletterMessage && (
                <div className={`alert ${newsletterSuccess ? 'alert-success' : 'alert-danger'} small mb-3`}>
                  {newsletterMessage}
                </div>
              )}
              
              <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  placeholder="tu@email.com"
                  className="newsletter-input"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={newsletterLoading}
                  required
                />
                <button 
                  type="submit" 
                  className="newsletter-btn"
                  disabled={newsletterLoading}
                >
                  {newsletterLoading ? "Suscribiendo..." : "Suscribirse"}
                </button>
              </form>
            </div>
          </div>

          <div className="blog-layout">
            {/* Main Content */}
            <div className="blog-main">
              {/* Featured Post */}
              {!searchTerm && !selectedCategory && (
                <>
                  <Link to={`/blog/${featuredPost.slug}`} className="featured-post">
                    <div className="featured-image-wrapper">
                      <img src={featuredPost.image} alt={featuredPost.title} />
                      <span className="featured-badge">Destacado</span>
                    </div>
                    <div className="featured-content">
                      <div className="post-meta">
                        <span className="category-badge">{featuredPost.category}</span>
                        <span className="post-date">{new Date(featuredPost.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        <span className="read-time">⏱️ {featuredPost.readTime}</span>
                      </div>
                      <h2 className="featured-title">{featuredPost.title}</h2>
                      <p className="featured-excerpt">{featuredPost.excerpt}</p>
                      <div className="post-footer">
                        <span className="post-author">Por {featuredPost.author}</span>
                        <span className="read-more">Leer más</span>
                      </div>
                    </div>
                  </Link>

                  <div className="blog-test-banner">
                    <h2>¿Tu dieta no funciona? Podría ser tu metabolismo 🧬</h2>
                    <p>Haz nuestro test de 2 minutos y descubre por qué no estás viendo resultados.</p>
                    <Link to="/test-metabolismo" className="blog-test-banner-btn">
                      EMPEZAR TEST GRATIS →
                    </Link>
                  </div>
                </>
              )}

              {/* Posts Grid */}
              <div className="posts-grid">
                {filteredPosts.length > 0 ? (
                  (searchTerm || selectedCategory ? filteredPosts : otherPosts).map(post => (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="post-card">
                      <div className="post-image">
                        <img src={post.image} alt={post.title} />
                        <span className="category-badge">{post.category}</span>
                      </div>
                      <div className="post-content">
                        <div className="post-meta-small">
                          <span>{new Date(post.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span>⏱️ {post.readTime}</span>
                        </div>
                        <h3 className="post-title">{post.title}</h3>
                        <p className="post-excerpt">{post.excerpt}</p>
                        <div className="post-tags">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="tag">#{tag}</span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="no-results">
                    <p className="no-results-icon">📝</p>
                    <p className="no-results-text">No se encontraron artículos</p>
                    <button 
                      className="clear-filters"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategory(null);
                      }}
                    >
                      Limpiar filtros
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="blog-sidebar">
              {/* Newsletter */}
              <div className="sidebar-widget newsletter-widget">
                <h3>📧 Newsletter</h3>
                <p>Recibe consejos de nutrición en tu email</p>
                
                {newsletterMessage && (
                  <div className={`alert ${newsletterSuccess ? 'alert-success' : 'alert-danger'} small mb-3`}>
                    {newsletterMessage}
                  </div>
                )}
                
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input 
                    type="email" 
                    placeholder="tu@email.com"
                    className="newsletter-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterLoading}
                    required
                  />
                  <button 
                    type="submit" 
                    className="newsletter-btn"
                    disabled={newsletterLoading}
                  >
                    {newsletterLoading ? "Suscribiendo..." : "Suscribirse"}
                  </button>
                </form>
              </div>

              {/* Popular Tags */}
              <div className="sidebar-widget">
                <h3>🏷️ Tags Populares</h3>
                <div className="tags-cloud">
                  {popularTags.map((tag, index) => (
                    <button
                      key={index}
                      className="tag-btn"
                      onClick={() => setSearchTerm(tag)}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA
              <div className="sidebar-widget cta-widget">
                <h3>📱 Descarga Wayo</h3>
                <p>Escanea tus comidas y calcula calorías automáticamente</p>
                <Link to="/" className="cta-btn">Descargar Gratis</Link>
              </div> */}
            </aside>
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  </>
  );
}
